// R4Pro — Capability taxonomy.
// Same taxonomy is used on user profiles (declared capabilities) and on request requirements,
// so matching can compare them directly. Extensible: just add entries to these arrays.

export const PROCESSES = [
  // Additive
  "3D Printing FDM",
  "3D Printing SLA / DLP",
  "3D Printing SLS / MJF",
  "3D Printing Metal (DMLS/SLM)",
  // Subtractive
  "CNC Milling",
  "CNC Turning",
  "CNC 5-Axis",
  "Laser Cutting",
  "Waterjet Cutting",
  "Plasma Cutting",
  "EDM",
  // Sheet / metal work
  "Sheet Metal",
  "Bending / Forming",
  "Welding TIG",
  "Welding MIG",
  "Welding Robotic",
  // Forming / molding
  "Injection Molding",
  "Vacuum Casting",
  "Sand Casting",
  "Investment Casting",
  "Composite Layup (Hand)",
  "Composite Autoclave",
  "RTM",
  // Finishing
  "Anodizing",
  "Powder Coating",
  "Painting",
  "Polishing",
  // Soft goods
  "Sewing (Industrial)",
  "Cutting (Textile)",
  "Embroidery",
  // Electronics
  "PCB Assembly (SMT)",
  "PCB Assembly (Through-Hole)",
  "Wire Harness",
  // General
  "Assembly",
  "Prototyping",
  "Reverse Engineering",
  "Design / CAD",
];

export const MATERIALS = [
  // Metals
  "Aluminum 6061",
  "Aluminum 7075",
  "Steel S235",
  "Steel S355",
  "Stainless 304",
  "Stainless 316L",
  "Titanium Gr.5",
  "Brass",
  "Copper",
  // Polymers
  "PLA",
  "ABS",
  "PETG",
  "Nylon PA12",
  "Nylon PA6",
  "PC (Polycarbonate)",
  "PP (Polypropylene)",
  "PEEK",
  "TPU",
  // Composites
  "Carbon T300",
  "Carbon T700",
  "Carbon T800",
  "Fiberglass",
  "Kevlar",
  // Textiles / other
  "Cotton",
  "Polyester Fabric",
  "Leather",
  "Wood MDF",
  "Wood Plywood",
  "Silicone",
  "Rubber EPDM",
];

// Common equipment presets — free-form allowed too.
export const EQUIPMENT_PRESETS = [
  { name: "3D Printer FDM", process: "3D Printing FDM" },
  { name: "3D Printer SLA", process: "3D Printing SLA / DLP" },
  { name: "3D Printer SLS", process: "3D Printing SLS / MJF" },
  { name: "CNC Mill 3-Axis", process: "CNC Milling" },
  { name: "CNC Mill 5-Axis", process: "CNC 5-Axis" },
  { name: "CNC Lathe", process: "CNC Turning" },
  { name: "Fiber Laser Cutter", process: "Laser Cutting" },
  { name: "CO2 Laser Cutter", process: "Laser Cutting" },
  { name: "Waterjet", process: "Waterjet Cutting" },
  { name: "TIG Welder", process: "Welding TIG" },
  { name: "MIG Welder", process: "Welding MIG" },
  { name: "Injection Press", process: "Injection Molding" },
  { name: "Autoclave", process: "Composite Autoclave" },
  { name: "Industrial Sewing Machine", process: "Sewing (Industrial)" },
  { name: "Embroidery Machine", process: "Embroidery" },
  { name: "SMT Line", process: "PCB Assembly (SMT)" },
];

// -------- Matching primitives --------

const norm = (x) => (x || "").toString().trim().toLowerCase();
const intersect = (a = [], b = []) => {
  const A = new Set(a.map(norm));
  return b.filter((x) => A.has(norm(x)));
};

export const MATCH = {
  DIRECT: "direct",
  PARTIAL: "partial",
  NONE: "none",
};

/**
 * Deterministic capability match between a request's requirements and a user profile.
 * Returns { level, matchedProcesses, matchedMaterials, missingProcesses, missingMaterials, explain }.
 *
 * Rules:
 *  - No requirements declared on the request → NONE (nothing to match against).
 *  - No capabilities declared on the profile → NONE (user should complete profile).
 *  - All required processes present AND (no material requirement OR at least one material matches) → DIRECT.
 *  - Any process OR material overlap → PARTIAL.
 *  - Otherwise → NONE.
 */
export function computeMatch(request, profile) {
  if (!request || !profile) return { level: MATCH.NONE, matchedProcesses: [], matchedMaterials: [], missingProcesses: [], missingMaterials: [], explain: "" };
  const reqProc = (request.requiredProcesses || []).filter(Boolean);
  const reqMat  = (request.requiredMaterials  || []).filter(Boolean);
  // Back-compat: fall back to legacy single fields
  if (reqProc.length === 0 && request.category) reqProc.push(request.category);
  if (reqMat.length === 0  && request.material) reqMat.push(request.material);

  const userProc = (profile.processes || []);
  const userMat  = (profile.materials || []);
  // Equipment can carry an implied process — treat it as a declared capability too
  const equipmentProcesses = (profile.equipment || [])
    .map((e) => e && e.process)
    .filter(Boolean);
  const allUserProc = [...userProc, ...equipmentProcesses];

  if (reqProc.length === 0 && reqMat.length === 0) {
    return { level: MATCH.NONE, matchedProcesses: [], matchedMaterials: [], missingProcesses: [], missingMaterials: [], explain: "no-requirements" };
  }
  if (allUserProc.length === 0 && userMat.length === 0) {
    return { level: MATCH.NONE, matchedProcesses: [], matchedMaterials: [], missingProcesses: reqProc, missingMaterials: reqMat, explain: "no-capabilities" };
  }

  const mp = intersect(allUserProc, reqProc);
  const mm = intersect(userMat, reqMat);
  const missingP = reqProc.filter((p) => !mp.map(norm).includes(norm(p)));
  const missingM = reqMat.filter((p) => !mm.map(norm).includes(norm(p)));

  const allProcessesCovered = reqProc.length > 0 && mp.length === reqProc.length;
  const materialsOk = reqMat.length === 0 || mm.length >= 1;

  let level = MATCH.NONE;
  if (allProcessesCovered && materialsOk) level = MATCH.DIRECT;
  else if (mp.length > 0 || mm.length > 0) level = MATCH.PARTIAL;

  return {
    level,
    matchedProcesses: mp,
    matchedMaterials: mm,
    missingProcesses: missingP,
    missingMaterials: missingM,
    explain: level,
  };
}
